import { segmentClient } from "@/lib/segment-client";
import { OnboardingProduct, type OnboardingStatus } from "@/models/onboarding-status.model";
import { type Business, BusinessImportSource, type BusinessStructureLabel, BusinessStructureLabels } from "@/models/business.model";
import { type User as Auth0User } from "@auth0/auth0-vue";
import { type EventProperties } from "@segment/analytics-next";
import { useStorage } from "@vueuse/core";
import { ref } from "vue";
import { maskSensitiveInput } from '@/lib/utils';

const OnboardingProducts = [
  OnboardingProduct.Factoring,
  BusinessImportSource.FleetCard,
];
const PROP_NOT_AVAILABLE = "N/A";

type OnboardingUserTraits = {
  'Application Status': string,
  Products: string;
}
type BusinessUserTraits = {
  Address: string;
  'Business City': string;
  DOT: string;
  name: string;
  'Phone Number': string;
  State: string;
  Zip: string;
  'Corporation Type': BusinessStructureLabel | typeof PROP_NOT_AVAILABLE;
  EIN: string;
  SSN: string;
}
type UserTraits = OnboardingUserTraits & BusinessUserTraits;

export enum AnalyticsEventActionStatus {
  Failure = 'failure',
  Success = 'success',
  Unknown = 'unknown',
}

enum AnalyticsEventNames {
  AGREEMENT_SIGNING = 'Agreement Signing',
  BUSINESS_DETAILS = 'Business details',
  BUSINESS_OWNERS = 'Business owners',
  USER_APPLICATION_DURATION = 'Application duration'
}

type BusinessDetailsEventProperties = {
  'Business email'?: string;
  'Business name': string;
  'Duration (s)'?: number;
  'Failure reason'?: string
  Status: AnalyticsEventActionStatus;
}

type AgreementSigningEventProperties = {
  'Details': string,
  'Duration (s)'?: number;
  'Email'?: string;
  'Failure reason'?: string
}

type UserApplicationDurationEventProperties = {
  'Business email'?: string;
  'Business name'?: string;
  'Duration (s)'?: number;
  'Fleet user' : boolean;
}

type BusinessOwnersDetailsEventProperties = {
  'Business owners count'?: number;
  'Business owners verified'?: number;
  'Duration'?: number;
  'Failure reason'?: string;
  Status: AnalyticsEventActionStatus;
}

function mapBusinessToTraits (business?: Business): BusinessUserTraits {
  return {
    Address: business?.address || PROP_NOT_AVAILABLE,
    'Business City': business?.city || PROP_NOT_AVAILABLE,
    DOT: business?.dot || PROP_NOT_AVAILABLE,
    name: business?.name || PROP_NOT_AVAILABLE,
    'Phone Number': business?.phoneNumber || PROP_NOT_AVAILABLE,
    State: business?.state || PROP_NOT_AVAILABLE,
    Zip: business?.zip || PROP_NOT_AVAILABLE,
    'Corporation Type': business?.structure && BusinessStructureLabels[business.structure] || PROP_NOT_AVAILABLE,
    EIN: business?.ein || PROP_NOT_AVAILABLE,
    SSN: maskSensitiveInput(business?.ssn, '*', 4) || PROP_NOT_AVAILABLE,
  };
}

async function logout () {
  try {
    await segmentClient.reset();
  } catch (error) {
    console.error('Failed logout analytics', error);
  } 
}

async function identifyFromAuth0User (currentAuth0User?: Auth0User) {
  try {
    if (!currentAuth0User || !currentAuth0User.email) {
      await segmentClient.reset();
      return;
    }
  
    const analyticsUserId = segmentClient.instance?.user().id();
  
    if (analyticsUserId && analyticsUserId === currentAuth0User.email) {
      return;
    }
  
    if (analyticsUserId && analyticsUserId !== currentAuth0User.email) {
      await segmentClient.reset();
    }
  
    await segmentClient.identify(currentAuth0User.email, {
      email: currentAuth0User.email,
    });
  } catch (error) {
    console.error('Failed publishing analytics', error);
  }
}

async function updateIdentityFromBusiness (business: Business) {
  try {
    await segmentClient.identify(
      segmentClient.instance?.user().id(),
      mapBusinessToTraits(business)
    );
  } catch (error) {
    console.error('Failed publishing analytics', error);
  }  
}

async function updateIdentityFromOnboardingStatus (onboardingStatus: null | OnboardingStatus) {
  try {
    if (!onboardingStatus) {
      return;
    }

    const traits: UserTraits = {
      'Application Status': onboardingStatus.applicationStatus,    
      Products: [
        onboardingStatus.product,
        onboardingStatus.business?.importedFrom,
      ]
        .filter(product => product && OnboardingProducts.includes(product))
        .join(','),
      ...mapBusinessToTraits(onboardingStatus.business),
    };

    await segmentClient.identify(
      segmentClient.instance?.user().id(),
      traits
    );
  } catch (error) {
    console.error('Failed publishing analytics', error);
  }
}

function createEventTracker<T extends EventProperties> (eventName: AnalyticsEventNames) {
  return async function (eventProperties: T) {
    try {
      await segmentClient.track(
        eventName,
        eventProperties
      );
    } catch (error) {
      console.log('Failed tracking event', eventName, error);
    }
  };
}

function createAgreementSigningEvent (
  startDateGenerateAgreement: null|string,
  user: string|undefined,
  failureReason?: null|string
) {
  let duration = 0;
    if (startDateGenerateAgreement && !isNaN(parseInt(startDateGenerateAgreement))) {
      duration = new Date().getTime() - parseInt(startDateGenerateAgreement);
    } 
    return {
      Details: failureReason
        ? `User couldn't sign the agreement`
        : `User has signed agreement and is now on the post submission UI`,
      'Duration (s)': duration
        ? Math.round(duration / 1e3)
        : 0,
      'Email': user
? user
: '',
      'Failure reason': failureReason
        ? failureReason
        : '',
      Status: failureReason
        ? AnalyticsEventActionStatus.Failure
        : AnalyticsEventActionStatus.Success,
    };
}

function createUserApplicationEvent (
  startDate: null|string,
  user: string|undefined,
  name: string|undefined,
  isFleetUser:boolean
) {
  let duration = 0;
    if (startDate && !isNaN(parseInt(startDate))) {
      duration = new Date().getTime() - parseInt(startDate);
    } 
    return {
      'Business email': user,
      'Business name': name,
      'Details': `User has completed the application`,
      'Duration (s)': duration
        ? Math.round(duration / 1e3)
        : 0,
      'Fleet user': isFleetUser,
      Status: AnalyticsEventActionStatus.Success,
    };
}

function getBusinessDetailsEventLastStatus (identifier?: string) {
  if (identifier) {
    return ref(AnalyticsEventActionStatus.Unknown);
  }

  return useStorage(
    [
      identifier,
      AnalyticsEventNames.BUSINESS_DETAILS,
    ]
      .join('|'),
    AnalyticsEventActionStatus.Unknown
  );
}

export default {
  AnalyticsEventActionStatus: AnalyticsEventActionStatus,
  createAgreementSigningEvent,
  createUserApplicationEvent,
  identifyFromAuth0User,
  logout,
  trackAgreementSigningEvent: createEventTracker<AgreementSigningEventProperties>(
    AnalyticsEventNames.AGREEMENT_SIGNING
  ),
  trackBusinessDetailsEvent: createEventTracker<BusinessDetailsEventProperties>(
    AnalyticsEventNames.BUSINESS_DETAILS
  ),
  trackBusinessOwnersDetailsEvent: createEventTracker<BusinessOwnersDetailsEventProperties>(
    AnalyticsEventNames.BUSINESS_OWNERS
  ),
  trackUserApplicationEvent: createEventTracker<UserApplicationDurationEventProperties>(
    AnalyticsEventNames.USER_APPLICATION_DURATION
  ),
  updateIdentityFromBusiness,
  updateIdentityFromOnboardingStatus,
  getBusinessDetailsEventLastStatus,
};
